import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Search, CheckCircle, Clock, ChevronRight, ChevronLeft, FileText, Truck, ShieldAlert, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FormSubmissionsPage() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all', 'logistics', 'safety'
  const perPage = 20;

  useEffect(() => {
    fetchSubmissions();
  }, [page]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      const headers = { Authorization: `Bearer ${token}` };

      const res = await axios.get(`${API}/forms/submissions?page=${page}&per_page=${perPage}`, { headers });
      setSubmissions(res.data.submissions || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const isLogisticsForm = (sub) => {
    const name = (sub.template_name || '').toLowerCase();
    return name.includes('bill of lading') || name.includes('bol') || 
           sub.template_category?.toLowerCase() === 'logistics';
  };

  const totalPages = Math.ceil(total / perPage);

  // Filter by category first, then by search
  const categoryFilteredSubmissions = submissions.filter(sub => {
    if (categoryFilter === 'all') return true;
    if (categoryFilter === 'logistics') return isLogisticsForm(sub);
    if (categoryFilter === 'safety') return !isLogisticsForm(sub);
    return true;
  });

  const filteredSubmissions = categoryFilteredSubmissions.filter(sub => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      sub.submitted_by?.toLowerCase().includes(searchLower) ||
      sub.submitted_by_email?.toLowerCase().includes(searchLower) ||
      sub.template_name?.toLowerCase().includes(searchLower) ||
      sub.data?.work_description?.toLowerCase().includes(searchLower) ||
      sub.data?.location_lsd?.toLowerCase().includes(searchLower) ||
      sub.data?.client_name?.toLowerCase().includes(searchLower) ||
      sub.data?.bol_number?.toLowerCase().includes(searchLower) ||
      sub.data?.charge_to_name?.toLowerCase().includes(searchLower) ||
      sub.data?.charge_to_location?.toLowerCase().includes(searchLower) ||
      sub.data?.driver_name?.toLowerCase().includes(searchLower) ||
      sub.data?.truck_number?.toLowerCase().includes(searchLower)
    );
  });

  // Count by category
  const logisticsCount = submissions.filter(isLogisticsForm).length;
  const safetyCount = submissions.filter(s => !isLogisticsForm(s)).length;

  const getSubmissionPreview = (sub) => {
    if (isLogisticsForm(sub)) {
      // BOL preview
      return {
        line1: sub.data?.charge_to_name || sub.data?.driver_name || '-',
        line2: sub.data?.charge_to_location || sub.data?.cargo_description || '-',
        badge: sub.data?.bol_number ? `BOL #${sub.data.bol_number}` : null
      };
    }
    // Safety form preview
    return {
      line1: sub.data?.work_description || '-',
      line2: sub.data?.location_lsd || sub.data?.client_name || '-',
      badge: null
    };
  };

  return (
    <div className="space-y-6" data-testid="form-submissions-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/dashboard/forms">
          <Button variant="outline" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-foreground">All Submissions</h1>
          <p className="text-muted-foreground">{total} total submissions</p>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={categoryFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setCategoryFilter('all')}
          className="gap-2"
        >
          <FileText className="h-4 w-4" />
          All ({submissions.length})
        </Button>
        <Button
          variant={categoryFilter === 'logistics' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setCategoryFilter('logistics')}
          className={`gap-2 ${categoryFilter === 'logistics' ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-500/50 text-blue-400 hover:bg-blue-500/10'}`}
        >
          <Truck className="h-4 w-4" />
          Logistics ({logisticsCount})
        </Button>
        <Button
          variant={categoryFilter === 'safety' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setCategoryFilter('safety')}
          className={`gap-2 ${categoryFilter === 'safety' ? 'bg-yellow-600 hover:bg-yellow-700' : 'border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10'}`}
        >
          <ShieldAlert className="h-4 w-4" />
          Safety ({safetyCount})
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={categoryFilter === 'logistics' 
              ? "Search by BOL #, customer, driver, truck..." 
              : categoryFilter === 'safety'
              ? "Search by name, location, client..."
              : "Search submissions..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Submissions List */}
      <Card className={categoryFilter === 'logistics' ? 'border-blue-500/30' : categoryFilter === 'safety' ? 'border-yellow-500/30' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {categoryFilter === 'logistics' ? (
              <Truck className="h-5 w-5 text-blue-400" />
            ) : categoryFilter === 'safety' ? (
              <ShieldAlert className="h-5 w-5 text-yellow-400" />
            ) : (
              <FileText className="h-5 w-5" />
            )}
            {categoryFilter === 'all' ? 'All Submissions' : 
             categoryFilter === 'logistics' ? 'Logistics Submissions' : 'Safety Submissions'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredSubmissions.length > 0 ? (
            <div className="space-y-2">
              {filteredSubmissions.map((sub) => {
                const isLogistics = isLogisticsForm(sub);
                const preview = getSubmissionPreview(sub);
                
                return (
                  <Link key={sub.id} to={`/dashboard/forms/view/${sub.id}`}>
                    <div className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                      isLogistics 
                        ? 'bg-blue-500/5 hover:bg-blue-500/10 border border-blue-500/20' 
                        : 'bg-yellow-500/5 hover:bg-yellow-500/10 border border-yellow-500/20'
                    }`}>
                      <div className="flex items-center gap-4">
                        {isLogistics ? (
                          <Truck className="h-5 w-5 text-blue-400 flex-shrink-0" />
                        ) : (
                          <ShieldAlert className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-foreground">{sub.template_name}</p>
                            {preview.badge && (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                isLogistics ? 'bg-blue-500/20 text-blue-300' : 'bg-yellow-500/20 text-yellow-300'
                              }`}>
                                {preview.badge}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                            <span>{sub.submitted_by}</span>
                            <span className="hidden sm:inline">•</span>
                            <span className="hidden sm:inline">{formatDate(sub.submitted_at).split(',')[0]}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 truncate max-w-md">
                            {preview.line1}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="text-right hidden md:block">
                          <p className="text-sm text-muted-foreground">{sub.data?.date || '-'}</p>
                          <p className="text-xs text-muted-foreground">{preview.line2}</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-12">
              No {categoryFilter !== 'all' ? categoryFilter : ''} submissions found
            </p>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
